import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import applicationDetailSignal from '../ApplicationDetail.signal';
import userAccountSignal from 'signals/UserAccount.signal';
import $appSettings from 'signals/AppSettings.signal';
import {
  fetchLoanProcessingBatches,
  createLoanProcessingBatch,
  addApplicationToBatch,
  submitLoanProcessingBatch,
  withdrawApplicationFromBatch,
} from 'libs/loanProcessingBatchApi';
import alertSignal from 'signals/Alert.signal';
import CloseModalButton from 'components/global/Constant/CloseModalButton';
import { isEdoAdminOrOwner } from 'libs/edoPermissions';
import {
  notifyApplicationLoanTrackingRefresh,
  notifyLoanProcessingBatchesDashboardDirty,
} from 'libs/loanProcessingBatchesDashboardDirty';
import LoanBatchRemovalNoteModal from 'components/views/Edo/LoanBatchRemovalNoteModal';
import { EDO_BATCH_PROCESSING_NAV_FEATURE } from 'components/global/Constant/componentProps';
import { isEdoFeatureExcludeVisible } from 'libs/edoNavFeatureVisibility';

type BatchMemberRow = {
  applicationId: number;
  lenderLoanNumber?: string | null;
  businessName?: string | null;
};

type DraftBatchRow = {
  id: number;
  name: string;
  status: string;
  fundingProgramId: number;
  members?: BatchMemberRow[];
};

function normalizedApplicationId(id: unknown): number | null {
  if (id == null) return null;
  const n = typeof id === 'number' ? id : parseInt(String(id), 10);
  return Number.isFinite(n) ? n : null;
}

function draftBatchContainingApplication(drafts: DraftBatchRow[], applicationId: number) {
  return (
    drafts.find((b) =>
      (b.members || []).some((m) => normalizedApplicationId(m.applicationId) === applicationId)
    ) ?? null
  );
}

const ApplicationLoanBatchPanel: React.FC = () => {
  const { id: applicationId, fundingProgram, currentTab, status: applicationStatus } =
    applicationDetailSignal.value;
  const { PORTAL_TYPES, APPLICATION_STATUS } = $appSettings.value.constants;
  const portalType = userAccountSignal.value?.userData?.account?.portalType;
  const isEdo = portalType === PORTAL_TYPES?.edo;
  const usesEdoTopNav =
    portalType === PORTAL_TYPES?.edo || portalType === PORTAL_TYPES?.state;
  const showManageBatchProcessing =
    usesEdoTopNav &&
    isEdoFeatureExcludeVisible(
      EDO_BATCH_PROCESSING_NAV_FEATURE,
      userAccountSignal.value.userData
    );
  const programId = fundingProgram?.id;
  const isDraftApplication = applicationStatus === APPLICATION_STATUS.inProgress;

  const [drafts, setDrafts] = useState<DraftBatchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalName, setCreateModalName] = useState('');
  const [addCurrentAppToNewBatch, setAddCurrentAppToNewBatch] = useState(true);
  const [withdrawModalMemberId, setWithdrawModalMemberId] = useState<number | null>(null);

  const loadDrafts = useCallback(async () => {
    if (!programId || !isEdo) return;
    setLoading(true);
    try {
      const all = await fetchLoanProcessingBatches({ status: 'DRAFT' });
      const forProgram = (all || []).filter((b: DraftBatchRow) => b.fundingProgramId === programId);
      setDrafts(forProgram);
    } catch {
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, [programId, isEdo]);

  useEffect(() => {
    if (applicationId && currentTab === 'loanTracking' && isEdo && programId) {
      loadDrafts();
    }
  }, [applicationId, currentTab, isEdo, programId, loadDrafts]);

  useEffect(() => {
    setSelectedBatchId('');
  }, [applicationId]);

  useEffect(() => {
    if (currentTab !== 'loanTracking' || !applicationId) return;
    const aid = normalizedApplicationId(applicationId);
    if (aid == null) return;
    const containing = draftBatchContainingApplication(drafts, aid);
    if (containing) {
      setSelectedBatchId(String(containing.id));
    }
  }, [drafts, applicationId, currentTab]);

  useEffect(() => {
    if (isDraftApplication) {
      setAddCurrentAppToNewBatch(false);
    }
  }, [isDraftApplication]);

  const canUse = useMemo(() => isEdo && !!applicationId && !!programId, [isEdo, applicationId, programId]);

  const userData = userAccountSignal.value?.userData;
  const canRemoveUi =
    !!userData &&
    (isEdoAdminOrOwner(userData) ||
      (userData.edoUserProgramAccesses || []).some((a) =>
        ['ADMIN', 'MANAGER', 'ANALYST', 'STAFF'].includes(a.role)
      ));

  const selectedBatch = useMemo(() => {
    if (!selectedBatchId) return null;
    const id = parseInt(selectedBatchId, 10);
    if (!id) return null;
    return drafts.find((d) => d.id === id) ?? null;
  }, [drafts, selectedBatchId]);

  const appAlreadyInSelectedBatch = useMemo(() => {
    if (!selectedBatch || applicationId == null) return false;
    const aid = typeof applicationId === 'number' ? applicationId : parseInt(String(applicationId), 10);
    if (!Number.isFinite(aid)) return false;
    return (selectedBatch.members || []).some((m) => m.applicationId === aid);
  }, [selectedBatch, applicationId]);

  useEffect(() => {
    if (!selectedBatchId) return;
    const id = parseInt(selectedBatchId, 10);
    if (!id || drafts.some((d) => d.id === id)) return;
    setSelectedBatchId('');
  }, [drafts, selectedBatchId]);

  const addToBatch = async () => {
    if (isDraftApplication) return;
    const bid = parseInt(selectedBatchId, 10);
    if (!bid || !applicationId || appAlreadyInSelectedBatch) return;
    setBusy(true);
    try {
      await addApplicationToBatch(bid, applicationId);
      alertSignal.update({ type: 'notification', message: 'Application added to batch.', variant: 'success' });
      notifyApplicationLoanTrackingRefresh({ applicationId });
      notifyLoanProcessingBatchesDashboardDirty();
      await loadDrafts();
    } catch (e: any) {
      alertSignal.update({
        type: 'notification',
        message: e?.message || 'Could not add to batch',
        variant: 'danger',
      });
    } finally {
      setBusy(false);
    }
  };

  const confirmWithdrawFromSelectedBatch = async (note: string) => {
    const bid = parseInt(selectedBatchId, 10);
    const memberApplicationId = withdrawModalMemberId;
    if (!bid || memberApplicationId == null) return;
    setBusy(true);
    try {
      await withdrawApplicationFromBatch(bid, memberApplicationId, note);
      alertSignal.update({ type: 'notification', message: 'Application removed from batch.', variant: 'success' });
      notifyApplicationLoanTrackingRefresh({ applicationId: memberApplicationId });
      notifyLoanProcessingBatchesDashboardDirty();
      setWithdrawModalMemberId(null);
      const viewingAid = normalizedApplicationId(applicationId);
      if (viewingAid != null && viewingAid === memberApplicationId) {
        setSelectedBatchId('');
      }
      await loadDrafts();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Remove failed';
      alertSignal.update({ type: 'notification', message: msg, variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  const withdrawModalBusy = withdrawModalMemberId != null && busy;

  const openCreateModal = () => {
    setCreateModalName('');
    setAddCurrentAppToNewBatch(true);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (busy) return;
    setShowCreateModal(false);
  };

  const handleCreateBatch = async () => {
    if (!programId || !createModalName.trim()) return;
    setBusy(true);
    try {
      const batch = await createLoanProcessingBatch({
        name: createModalName.trim(),
        fundingProgramId: programId,
      });
      if (
        addCurrentAppToNewBatch &&
        batch?.id &&
        applicationId &&
        !isDraftApplication
      ) {
        await addApplicationToBatch(batch.id, applicationId);
        notifyApplicationLoanTrackingRefresh({ applicationId });
      }
      setShowCreateModal(false);
      setCreateModalName('');
      alertSignal.update({
        type: 'notification',
        message:
          addCurrentAppToNewBatch && applicationId && !isDraftApplication
            ? 'Batch created and application added.'
            : 'Batch created.',
        variant: 'success',
      });
      notifyLoanProcessingBatchesDashboardDirty();
      await loadDrafts();
      if (batch?.id) {
        setSelectedBatchId(String(batch.id));
      }
    } catch (e: any) {
      alertSignal.update({
        type: 'notification',
        message: e?.message || 'Could not create batch',
        variant: 'danger',
      });
    } finally {
      setBusy(false);
    }
  };

  const submitSelected = async () => {
    const bid = parseInt(selectedBatchId, 10);
    if (!bid) return;
    setBusy(true);
    try {
      await submitLoanProcessingBatch(bid);
      alertSignal.update({ type: 'notification', message: 'Batch submitted for management review.', variant: 'success' });
      notifyLoanProcessingBatchesDashboardDirty();
      if (applicationId != null) {
        notifyApplicationLoanTrackingRefresh({ applicationId });
      }
      await loadDrafts();
    } catch (e: any) {
      alertSignal.update({
        type: 'notification',
        message: e?.message || 'Could not submit batch',
        variant: 'danger',
      });
    } finally {
      setBusy(false);
    }
  };

  if (!canUse) return null;

  return (
    <>
      <Row className="mb-16 align-items-center">
        <Col>
          <h5 className="mb-0">Loan Batch Processing (CPCFA workflow)</h5>
        </Col>
        <Col xs="auto" className="text-right">
          {showManageBatchProcessing ? (
            <Link
              to="/edo/manage-batch-processing"
              className="btn btn-outline-primary rounded mr-2 align-middle"
            >
              Manage Batch Processing
            </Link>
          ) : null}
          <Button
            variant="primary"
            className="rounded"
            onClick={openCreateModal}
            disabled={busy || isDraftApplication}
          >
            Create Batch
          </Button>
        </Col>
      </Row>
      <div className="edo-loan-batch-panel">
        <p className="edo-loan-batch-panel__intro">
          Each batch is for one funding program only: every application you add must belong to that same program. Group up
          to 10 loans per batch before management approval. Submit the batch from here when ready; use the EDO dashboard
          for management and outgoing steps.
        </p>
        {loading ? (
          <p className="edo-loan-batch-panel__hint mb-0">Loading Draft Batches…</p>
        ) : (
          <>
            <Form.Group className="mb-16">
              <Form.Label className="edo-loan-batch-panel__field-label d-block">Draft Batch for This Program</Form.Label>
              <Form.Control
                as="select"
                value={selectedBatchId}
                onChange={(e) => setSelectedBatchId(e.target.value)}
                disabled={busy || isDraftApplication}
              >
                <option value="">Select Batch…</option>
                {drafts.map((d) => {
                  const n = d.members?.length ?? 0;
                  return (
                    <option key={d.id} value={d.id}>
                      {d.name} (ID {d.id}) — {n} loan{n === 1 ? '' : 's'}
                    </option>
                  );
                })}
              </Form.Control>
            </Form.Group>
            {selectedBatch ? (
              <div className="mb-16">
                <div className="edo-loan-batch-panel__section-title">Applications in This Batch</div>
                {(selectedBatch.members || []).length === 0 ? (
                  <p className="edo-loan-batch-panel__hint mb-0">
                    No applications yet. Use &quot;Add This Application to Batch&quot; below, or open other applications on
                    Loan Tracking and add them to this batch.
                  </p>
                ) : (
                  <>
                    <ul className="list-unstyled mb-8 small">
                      {(selectedBatch.members || []).map((m) => (
                        <li key={m.applicationId} className="mb-8 d-flex flex-wrap align-items-center">
                          <Link
                            to={`/edo/portfolio/${m.applicationId}?tab=loanTracking`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={m.applicationId === applicationId ? 'font-weight-bold' : ''}
                          >
                            App #{m.applicationId}
                            {m.businessName ? ` — ${m.businessName}` : ''}
                            {m.lenderLoanNumber ? ` — ${m.lenderLoanNumber}` : ''}
                            {m.applicationId === applicationId ? ' (this application)' : ''}
                          </Link>
                          {canRemoveUi ? (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="ml-8 rounded"
                              disabled={busy}
                              onClick={() => setWithdrawModalMemberId(m.applicationId)}
                            >
                              Remove from Batch
                            </Button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ) : null}
            <div className="edo-loan-batch-panel__actions d-flex flex-wrap align-items-center">
              <Button
                variant="outline-primary"
                size="sm"
                className="rounded mr-8 mb-8"
                disabled={
                  busy ||
                  isDraftApplication ||
                  !selectedBatchId ||
                  appAlreadyInSelectedBatch
                }
                onClick={addToBatch}
              >
                Add This Application to Batch
              </Button>
              {selectedBatchId && appAlreadyInSelectedBatch ? (
                <span className="edo-loan-batch-panel__hint mb-8 mr-8">This application is already in the selected batch.</span>
              ) : null}
              <Button
                variant="primary"
                size="sm"
                className="rounded mb-8"
                disabled={
                  busy ||
                  isDraftApplication ||
                  !selectedBatchId ||
                  (selectedBatch?.members?.length ?? 0) === 0
                }
                onClick={submitSelected}
              >
                Submit Batch for L1 Review
              </Button>
            </div>
          </>
        )}
      </div>

      <Modal show={showCreateModal} onHide={closeCreateModal} centered>
        <Modal.Header className="d-flex align-items-center justify-content-between">
          <Modal.Title className="mb-0">Create Batch</Modal.Title>
          <CloseModalButton onCloseModal={closeCreateModal} />
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Batch Name</Form.Label>
            <Form.Control
              value={createModalName}
              onChange={(e) => setCreateModalName(e.target.value)}
              placeholder="e.g. TJH 03.23.26 #1 SB Loans"
              autoFocus
              disabled={busy}
            />
          </Form.Group>
          <Form.Check
            type="checkbox"
            id="loan-batch-add-current-app"
            label="Add This Application to the New Batch"
            checked={isDraftApplication ? false : addCurrentAppToNewBatch}
            onChange={(e) => setAddCurrentAppToNewBatch(e.target.checked)}
            disabled={busy || isDraftApplication}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" className="rounded" onClick={closeCreateModal} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" className="rounded" disabled={busy || !createModalName.trim()} onClick={handleCreateBatch}>
            {busy ? 'Creating…' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      <LoanBatchRemovalNoteModal
        show={withdrawModalMemberId != null}
        variant="withdrawOne"
        onHide={() => {
          if (!withdrawModalBusy) setWithdrawModalMemberId(null);
        }}
        onConfirm={confirmWithdrawFromSelectedBatch}
        busy={withdrawModalBusy}
      />
    </>
  );
};

export default ApplicationLoanBatchPanel;
